import store from 'app/store';
import { AppProvider } from 'context/AppProvider';
import { SocketProvider } from 'context/SocketProvider';
import PrimeReact from 'primereact/api';
import PageProgress from 'react-page-progress';
import { Provider } from 'react-redux';
import { AuthenticationProvider, OidcSecure, useReactOidc } from '@axa-fr/react-oidc-context';
import AppTranslations from 'translations/AppTranslations';
import { oidcConfiguration, logoutOidc } from './oidc';
import { BrowserRouter, matchPath, useHistory, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import EmptyPage from 'components/EmptyPage/EmptyPage';
import { routerMain } from 'constants/routerMain';
import { Routes, RoutesEContract } from 'routers/router';
import 'primereact/resources/themes/bootstrap4-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'assets/style/layout.scss';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { initGA, trackPageView } from 'services/analytics';

const RouteTracker = () => {
    const location = useLocation();
    const prevLocation = useRef('');

    useEffect(() => {
        const fullPath = location.pathname;
        if (prevLocation.current !== fullPath) {
            prevLocation.current = fullPath;
            trackPageView(fullPath);
        }
    }, [location]);

    return null;
};

const PUBLIC_ROUTE_PREFIXES = [
    routerMain.SIGN_NUMBER,
    routerMain.CHECK_SIGN,
    routerMain.EMPTY,
    routerMain.CLIENT_SIGN,
    routerMain.SCREEN_SIGN,
    routerMain.SEARCH_SCREEN_SIGN,
    routerMain.SCREEN_LABOR_SIGN,
    routerMain.SEARCH_SCREEN_LABOR_SIGN,
    routerMain.SCREEN_SIGN_CONTRACT_DOC,
    routerMain.SEARCH_SCREEN_SIGN_CONTRACT_DOC,
    routerMain.SCREEN_SIGN_SUBMISSION,
    routerMain.SEARCH_SCREEN_SIGN_SUBMISSION,
    routerMain.SEARCH_SIGN_DOCUMENT,
    routerMain.HPNY,
    routerMain.ADD_INFORMATION,
    routerMain.PAGE_401,
    routerMain.SHORT_LINK,
];

const isPublicRoute = (pathname) =>
    PUBLIC_ROUTE_PREFIXES.some((prefix) =>
        prefix === routerMain.SHORT_LINK
            ? pathname === '/s' || pathname.startsWith('/s/')
            : pathname.startsWith(prefix)
    );
const RETURN_LOCATION_KEY = 'econ.auth.return_location';
const AUTH_PATHS = ['/authentication/callback', '/authentication/silent_callback'];

const AuthenticatedApp = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const { oidcUser, isLoading } = useReactOidc();
    const restored = useRef(false);

    useEffect(() => {
        const excluded =
            location.pathname === '/' || AUTH_PATHS.includes(location.pathname) || isPublicRoute(location.pathname);
        if (!excluded) {
            sessionStorage.setItem(
                RETURN_LOCATION_KEY,
                JSON.stringify({ pathname: location.pathname, search: location.search, hash: location.hash })
            );
        }
    }, [location]);

    useEffect(() => {
        const isLoginReturn = location.pathname === '/' || AUTH_PATHS.includes(location.pathname);
        if (isLoading || !oidcUser || restored.current || !isLoginReturn) return;
        restored.current = true;

        const stored = sessionStorage.getItem(RETURN_LOCATION_KEY);
        sessionStorage.removeItem(RETURN_LOCATION_KEY);
        if (!stored) {
            history.replace(routerMain.HOME);
            return;
        }

        let target;
        try {
            target = JSON.parse(stored);
        } catch (error) {
            history.replace(routerMain.HOME);
            return;
        }

        const routes = [...Routes, ...RoutesEContract];
        const valid =
            target?.pathname?.startsWith('/') &&
            !target.pathname.startsWith('//') &&
            !AUTH_PATHS.includes(target.pathname) &&
            !isPublicRoute(target.pathname) &&
            routes.some((route) => matchPath(target.pathname, { path: route.path, exact: route.exact }));

        history.replace(valid ? `${target.pathname}${target.search || ''}${target.hash || ''}` : routerMain.HOME);
    }, [history, isLoading, location.pathname, oidcUser]);

    return children;
};

const NotAuthenticated = () => {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const message = params.get('message') || '';

        if (message) {
            try {
                Object.keys(sessionStorage).forEach((k) => k.startsWith('oidc.') && sessionStorage.removeItem(k));
                Object.keys(localStorage).forEach((k) => k.startsWith('oidc.') && localStorage.removeItem(k));
            } catch (e) {
                // ignore
            }
            logoutOidc();
        }
    }, []);

    return <EmptyPage />;
};

const windowEnvConfig = window.runConfig;

const WrapApp = () => {
    const LazyApp = lazy(() => import('./App'));
    PrimeReact.ripple = true;
    PrimeReact.autoZIndex = true;

    const GOOGLE_CLIENT_ID = windowEnvConfig.REACT_APP_CLIENT_ID_GOOGLE;
    const queryClient = new QueryClient();

    useEffect(() => {
        initGA();
        queryClient.setDefaultOptions({
            queries: {
                staleTime: 10000,
                keepPreviousData: true,
                refetchOnWindowFocus: false,
                retry: false,
            },
        });
    }, [queryClient]);

    useEffect(() => {
        const handleAutoTitleHover = (e) => {
            const target = e.target?.closest?.(
                '.p-multiselect-token, .p-multiselect-token-label, .p-multiselect-label:not(.p-placeholder), .p-dropdown-label:not(.p-placeholder), .p-multiselect-item, .p-dropdown-item'
            );
            if (target && !target.getAttribute('title')) {
                const text = (target.textContent || '').trim();
                if (text) {
                    target.setAttribute('title', text);
                }
            }
        };

        document.addEventListener('mouseover', handleAutoTitleHover, { passive: true });
        return () => document.removeEventListener('mouseover', handleAutoTitleHover);
    }, []);

    return (
        <div className="WrapApp">
            <ErrorBoundary>
                <BrowserRouter>
                    <RouteTracker />
                    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                        <AppTranslations>
                            <SocketProvider>
                                <Provider store={store}>
                                    <QueryClientProvider client={queryClient}>
                                        <AppProvider>
                                            <AuthenticationProvider
                                                configuration={oidcConfiguration}
                                                authenticating={EmptyPage}
                                                callbackComponentOverride={EmptyPage}
                                                notAuthenticated={NotAuthenticated}
                                            >
                                                <PageProgress color="var(--primary)" height={4} className="PageProgress" />
                                                <Suspense fallback={<EmptyPage />}>
                                                    <AuthenticatedApp>
                                                        {isPublicRoute(window.location.pathname) ? (
                                                            <LazyApp />
                                                        ) : (
                                                            <OidcSecure>
                                                                <LazyApp />
                                                            </OidcSecure>
                                                        )}
                                                    </AuthenticatedApp>
                                                    <ReactQueryDevtools initialIsOpen />
                                                </Suspense>
                                            </AuthenticationProvider>
                                        </AppProvider>
                                    </QueryClientProvider>
                                </Provider>
                            </SocketProvider>
                        </AppTranslations>
                    </GoogleOAuthProvider>
                </BrowserRouter>
            </ErrorBoundary>
        </div>
    );
};

export default WrapApp;
